import type { HistoricalFilter, HistoricalPort } from '../../consumer/historical/contract';
import { HISTORICAL_METHODS, validHistoricalRunId } from '../../consumer/historical/contract';
import { createMonitoringRequest, MonitoringReadError } from '../http';
import { parseHistoricalRows, parseHistoricalRun, parseHistoricalRuns } from './wire';
export class HistoricalRequestError extends MonitoringReadError {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super('HTTP');
  }
}
export function historicalQuery(f: HistoricalFilter): string {
  if (
    !Number.isSafeInteger(f.offset) ||
    f.offset < 0 ||
    f.offset > 1000000 ||
    !Number.isSafeInteger(f.limit) ||
    f.limit < 1 ||
    f.limit > 100 ||
    (f.stationId && !/^[A-Za-z0-9_-]{1,64}$/.test(f.stationId)) ||
    (f.metric && !['TEMPERATURE', 'WIND_SPEED'].includes(f.metric)) ||
    (f.method && !HISTORICAL_METHODS.some((m) => m === f.method)) ||
    (f.horizonHours && !['1', '3', '6'].includes(f.horizonHours))
  )
    throw new Error('Invalid historical filter');
  for (const time of [f.from, f.to])
    if (
      time &&
      (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(time) ||
        !Number.isFinite(Date.parse(time)))
    )
      throw new Error('Invalid UTC time');
  if (f.from && f.to && Date.parse(f.from) >= Date.parse(f.to))
    throw new Error('Invalid time range');
  const p = new URLSearchParams();
  for (const [key, value] of Object.entries(f)) if (value !== '') p.set(key, String(value));
  return p.toString();
}
export function createHistoricalAdapter(options: {
  baseOrigin: string;
  fetchImpl?: typeof fetch;
}): HistoricalPort {
  const request = createMonitoringRequest(options);
  const root = '/read-models/normal-monitoring/historical';
  const failure = (status: number, v: unknown) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return new MonitoringReadError('WIRE');
    const o = v as Record<string, unknown>;
    return Object.keys(o).sort().join(',') === 'error,message' &&
      typeof o.error === 'string' &&
      typeof o.message === 'string'
      ? new HistoricalRequestError(status, o.error)
      : new MonitoringReadError('WIRE');
  };
  const id = (value: string) => {
    if (!validHistoricalRunId(value)) throw new Error('Invalid historical run identity');
    return value;
  };
  return {
    runs: async (filter, signal) => {
      const p = await request(
        `${root}/runs?${historicalQuery(filter)}`,
        parseHistoricalRuns,
        signal,
        undefined,
        failure,
      );
      if (p.offset !== filter.offset || p.limit !== filter.limit)
        throw new MonitoringReadError('WIRE');
      return p;
    },
    run: async (value, signal) => {
      const r = await request(
        `${root}/runs/${id(value)}`,
        parseHistoricalRun,
        signal,
        undefined,
        failure,
      );
      if (r.runId !== value) throw new MonitoringReadError('WIRE');
      return r;
    },
    rows: async (value, filter, signal) => {
      const p = await request(
        `${root}/runs/${id(value)}/rows?${historicalQuery(filter)}`,
        parseHistoricalRows,
        signal,
        undefined,
        failure,
      );
      if (
        p.offset !== filter.offset ||
        p.limit !== filter.limit ||
        p.items.some(
          (r) =>
            (filter.stationId && r.stationId !== filter.stationId) ||
            (filter.metric && r.metric !== filter.metric) ||
            (filter.method && r.method !== filter.method) ||
            (filter.horizonHours && r.horizonHours !== Number(filter.horizonHours)) ||
            (filter.from && Date.parse(r.targetAt) < Date.parse(filter.from)) ||
            (filter.to && Date.parse(r.targetAt) >= Date.parse(filter.to)),
        )
      )
        throw new MonitoringReadError('WIRE');
      return p;
    },
  };
}
